import * as React from 'react';
import { Alert, Button, Form, FormControl, FormGroup, FormLabel, Modal, Spinner, Stack } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getConfigOrThrow, selectUser } from 'gossamer-front-end';

export const postChameleonRequest = async (path: 'chameleon-login' | 'chameleon-logout', body: object = {}) => {
  const response = await fetch(`${getConfigOrThrow('gossamer-be-origin')}/gossamer/main/${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.status !== 'OK') {
    throw new Error(data.message || `Request failed with status ${response.status}`);
  }
  return data as { status: 'OK'; message?: string };
};

export const ChameleonLogin = (): React.JSX.Element => {
  const user = useSelector(selectUser);
  const [searchParams] = useSearchParams();
  const [targetUserEmail, setTargetUserEmail] = React.useState<string>(searchParams.get('email') || '');
  const [isLoading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>('');
  const navigate = useNavigate();

  const handleFormSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!targetUserEmail.trim()) {
      setError('Please enter the email address of the user to log in as');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await postChameleonRequest('chameleon-login', { targetUserEmail: targetUserEmail.trim() });
      // full reload so that all cached data is discarded and the new session is picked up
      window.location.href = '/admin';
    } catch (err) {
      setError((err as Error).message || 'Failed to perform chameleon login');
      setLoading(false);
    }
  };

  return (
    <Modal show={true} backdrop="static" centered={true} size="lg">
      <Modal.Header>
        <Modal.Title>Chameleon Login (Staff Only)</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {user?.roleId !== 'staff' ? (
          <Alert variant="danger">You must be logged in as a staff user to use chameleon login.</Alert>
        ) : (
          <>
            <Alert variant="warning">
              <strong>Warning:</strong> This logs you in as another user, so that you see and can do exactly what they
              can. Use responsibly and only for legitimate support purposes. Use the banner shown while logged in as the
              user to return to your own account.
            </Alert>
            {error && <Alert variant="danger">{error}</Alert>}
          </>
        )}

        <Form onSubmit={handleFormSubmit}>
          <Stack gap={3}>
            <FormGroup controlId="targetUserEmail">
              <FormLabel>Email address of the user to log in as</FormLabel>
              <FormControl
                type="email"
                value={targetUserEmail}
                onChange={(event) => setTargetUserEmail(event.target.value)}
                placeholder="user.email@example.com"
                disabled={isLoading || user?.roleId !== 'staff'}
              />
            </FormGroup>

            <Stack direction="horizontal" gap={2} style={{ justifyContent: 'flex-end' }}>
              <Button variant="secondary" onClick={() => navigate(-1)} disabled={isLoading}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={isLoading || user?.roleId !== 'staff'}>
                {isLoading && (
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                )}
                {isLoading ? 'Logging in...' : 'Log in as User'}
              </Button>
            </Stack>
          </Stack>
        </Form>
      </Modal.Body>
    </Modal>
  );
};
